import React, { AriaAttributes, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from 'helpers/Analytics';
import { ITrackEventData } from 'interfaces/analytics';
import { useStyles } from './LinkButton.styles';
import { useTheme } from '@mui/material';

interface ILinkButtonAria extends AriaAttributes {
  role?: string;
}

export interface ILinkButton {
  ariaProps?: ILinkButtonAria;
  ariaLabel?: string; //TODO should be replaced with ariaProps
  ariaLabelledby?: string; //TODO should be replaced with ariaProps
  children: JSX.Element | (JSX.Element | boolean)[] | string | boolean;
  classNames?: { link?: string; container?: string; font?: string };
  isDark?: boolean;
  isExternal?: boolean;
  outOfReact?: boolean;
  target?: string | undefined;
  onClick?: (e?: any) => void;
  onMouseLeave?: () => void;
  tabIndex?: number;
  to?: string;
  trackEventData?: ITrackEventData;
  type?: 'submit' | 'reset' | 'button';
  variant?: LinkButtonType;
  dataAutomation?: string;
  disabled?: boolean;
  isPdf?: boolean;
  color?: string;
  rounded?: boolean;
}

export enum LinkButtonType {
  simple = 'simple',
  simpleEVAutos = 'simpleEVAutos',
  filled = 'filled',
  red = 'red',
  redOutlined = 'redOutlined',
  blackOutlined = 'blackOutlined',
  blueOutlined = 'blueOutlined',
  black = 'black',
  text = 'text',
  multiLineTextRed = 'multiLineTextRed',
  containedText = 'containedText',
  containedTextRed = 'containedTextRed',
  underlinedText = 'underlinedText',
  underlinedTextRed = 'underlinedTextRed',
  evAutos = 'evAutos',
  evAutosRound = 'evAutosRound',
}

export function LinkButton({
  ariaProps,
  ariaLabel,
  ariaLabelledby,
  children,
  classNames,
  isExternal,
  outOfReact,
  isDark,
  target,
  onClick,
  tabIndex,
  to,
  trackEventData,
  type,
  variant,
  dataAutomation,
  disabled,
  isPdf,
  rounded,
  color,
}: ILinkButton): JSX.Element {
  const classes = useStyles({
    isPageDark: isDark || false,
    buttonColor: color,
  });
  const theme = useTheme();
  const isHondaThemed = theme.themeName === 'honda';
  const ariaLabelProp = ariaLabel ? { 'aria-label': ariaLabel } : {};
  const ariaLabelledbyProp = ariaLabelledby
    ? { 'aria-labelledby': ariaLabelledby }
    : {};
  const linkType = variant ? classes[variant] : classes.simple;
  const defaultFont = isHondaThemed ? 'atm-caption-10' : 'acr-caption-10';
  const fontClass = classNames?.font
    ? `${isHondaThemed ? classes.text_inherit : ''} ${classNames.font}`
    : '';
  const hasMultiLineStyle = variant === LinkButtonType.multiLineTextRed;
  if (isPdf && to) {
    to = to.replace('/sitecore/media-library/', '/-/media/');
    to = to.concat('.pdf?cache-busting=1.1.1');
  }
  function onClickHandler(e: MouseEvent): void {
    onClick && onClick();
    if (trackEventData) {
      trackEventData.type = trackEventData.type || 'cta';
      trackEvent(trackEventData);
    }
  }

  if (to) {
    if (isExternal || outOfReact) {
      return (
        <div className={classNames?.container}>
          <a
            draggable={false}
            target={target}
            className={`${classes.button_base} ${linkType} ${
              rounded ? classes.roundedButton : ''
            } ${classNames?.link} ${defaultFont} t-link-button`}
            tabIndex={tabIndex || 0}
            onClick={onClickHandler}
            data-automation={dataAutomation}
            href={to}
            react-tracking={trackEventData ? '' : undefined}
            {...ariaProps}
          >
            <span
              className={`cta-focusable ${classes.focusable}`}
              tabIndex={-1}
            ></span>
            <div className={`cta-label ${classes.label} ${fontClass}`}>
              {hasMultiLineStyle ? <span>{children}</span> : children}
            </div>
          </a>
        </div>
      );
    } else {
      return (
        <div className={classNames?.container}>
          <Link
            draggable={false}
            to={to}
            target={target}
            className={`${fontClass} ${linkType} ${
              rounded ? classes.roundedButton : ''
            } ${classNames?.link} ${classes.base} ${defaultFont} t-link-button`}
            tabIndex={tabIndex || 0}
            onClick={onClickHandler}
            data-automation={dataAutomation}
            react-tracking={trackEventData ? '' : undefined}
            {...ariaProps}
            {...ariaLabelProp}
            {...ariaLabelledbyProp}
          >
            <span
              className={`cta-focusable ${classes.focusable}`}
              tabIndex={-1}
            ></span>
            <div className={`cta-label ${classes.label} ${fontClass}`}>
              {hasMultiLineStyle ? <span>{children}</span> : children}
            </div>
          </Link>
        </div>
      );
    }
  } else {
    return (
      <button
        draggable={false}
        onClick={onClickHandler}
        className={`${classes.button_base} ${linkType} ${
          rounded ? classes.roundedButton : ''
        } ${classNames?.container} ${
          classNames?.link
        } ${defaultFont} t-link-button`}
        tabIndex={tabIndex || 0}
        type={type}
        data-automation={dataAutomation}
        disabled={disabled}
        react-tracking={trackEventData ? '' : undefined}
        {...ariaProps}
        {...ariaLabelProp}
        {...ariaLabelledbyProp}
      >
        <span
          className={`cta-focusable ${classes.focusable}`}
          tabIndex={-1}
        ></span>
        <div className={`cta-label ${classes.label} ${fontClass}`}>
          {hasMultiLineStyle ? <span>{children}</span> : children}
        </div>
      </button>
    );
  }
}
